import axios, { AxiosInstance } from 'axios'
import { v4 as uuidv4 } from 'uuid'
import {
  ISession,
  StreamCallbacks,
  ThinkingStep,
  SendProjectMessageParams,
  SendTenantMessageParams,
  GetLatestSessionParams,
  CreateUserSessionParams,
  mapSessionResponse,
} from './models'
import { getCopaUserToken } from './copaUserToken'

export interface CopaProactiveResponseCallbacks {
  onSessionCreated: (sessionId: string) => void
  onMessageAdded: (messageId: string) => void
  onChunk: (chunk: string) => void
  onFirstChunk: () => void
  onThinking: (step: ThinkingStep) => void
  onThinkingDone: (durationMessage: string) => void
  onError: (messageId: string) => void
}

const AUTH_TOKEN = import.meta.env.VITE_APP_GEN_AI_AUTH_TOKEN

const copaHeaders = async () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${AUTH_TOKEN}`,
  'x-user-token': await getCopaUserToken(),
})

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_GEN_AI_URL,
})

api.interceptors.request.use(async (config) => {
  config.headers.set(await copaHeaders())
  return config
})

// ==================== SHARED SSE HELPER ====================

const readSSEStream = async (response: Response, callbacks: StreamCallbacks): Promise<void> => {
  const { onChunk, onThinking, onThinkingDone, onSessionInfo, onDone, onError } = callbacks

  if (!response.body) {
    throw new Error('Response body is null')
  }

  const contentLength = response.headers.get('Content-Length')
  if (contentLength === '0') {
    if (onError) {
      onError(new Error('No response from assistant. The AI service may be temporarily unavailable.'))
    }
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()

  let buffer = ''
  let currentEventType = ''
  let receivedContent = false

  let doneCalled = false
  let streamActive = true
  while (streamActive) {
    const { value, done } = await reader.read()

    if (done) {
      streamActive = false
      break
    }

    buffer += decoder.decode(value, { stream: true })

    let newlineIndex
    while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
      const line = buffer.substring(0, newlineIndex).trim()
      buffer = buffer.substring(newlineIndex + 1)

      if (line.startsWith('event:')) {
        currentEventType = line.substring(6).trim()
      } else if (line.startsWith('data:')) {
        try {
          const jsonStr = line.substring(5).trim()
          const json = JSON.parse(jsonStr)

          switch (currentEventType) {
            case 'delta':
              if (json.v) {
                receivedContent = true
                onChunk(json.v)
              }
              break

            case 'thinking':
              if (onThinking && json.step) {
                onThinking({ step: json.step, type: json.type ?? 'sequential', status: json.status })
              }
              break

            case 'thinking_done':
              if (onThinkingDone) {
                onThinkingDone(json.step)
              }
              break

            case 'info':
              if (json.session_id && onSessionInfo) {
                onSessionInfo(json.session_id)
              }
              break

            case 'done':
              if (onDone && !doneCalled) {
                doneCalled = true
                onDone()
              }
              break

            case 'error':
              if (json.message && onError) {
                onError(new Error(json.message))
              }
              break
          }
        } catch (e) {
          console.error('Error parsing SSE data:', e)
        }
        currentEventType = ''
      }
    }
  }

  if (!receivedContent) {
    if (onError) {
      onError(new Error('No response from assistant. The AI service may be temporarily unavailable.'))
    }
    return
  }

  if (onDone && !doneCalled) {
    onDone()
  }
}

// ==================== KNOWLEDGE BASE ====================

export interface LoadKnowledgeBaseParams {
  userId: string
  tenantId: string
  projectId: string
}

export const loadKnowledgeBase = async (params: LoadKnowledgeBaseParams): Promise<void> => {
  // NOTE: Skip long refreshes in local mode (not using COPA anyways there)
  if (import.meta.env.VITE_APP_MODE === 'local') {
    return
  }

  const { userId, tenantId, projectId } = params
  const url = `/chatbot/users/${userId}/tenants/${tenantId}/projects/${projectId}/load`
  const fullUrl = `${api.defaults.baseURL}${url}`

  const response = await fetch(fullUrl, {
    method: 'POST',
    headers: await copaHeaders(),
  })

  if (!response.ok) {
    throw new Error(`Server responded with ${response.status}: ${response.statusText}`)
  }

  if (!response.body) {
    throw new Error('Response body is null')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let currentEventType = ''

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    let newlineIndex
    while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
      const line = buffer.substring(0, newlineIndex).trim()
      buffer = buffer.substring(newlineIndex + 1)

      if (line.startsWith('event:')) {
        currentEventType = line.substring(6).trim()
      } else if (line.startsWith('data:')) {
        try {
          const json = JSON.parse(line.substring(5).trim())
          if (currentEventType === 'info' && json.status === 'Load Finished.') {
            reader.cancel()
            return
          }
        } catch (e) {
          console.error('Error parsing SSE data:', e)
        }
        currentEventType = ''
      }
    }
  }

  throw new Error('Knowledge base load stream ended without a "Load Finished." confirmation')
}

// ==================== SESSION MANAGEMENT ====================

export const createUserSession = async (params: CreateUserSessionParams): Promise<ISession> => {
  const { userId } = params
  const response = await api.post(`/chatbot/users/${userId}/sessions`)
  return mapSessionResponse(response.data)
}

export const getLatestSession = async (params: GetLatestSessionParams): Promise<ISession | null> => {
  const { userId } = params
  const url = `/chatbot/users/${userId}/sessions/latest`

  try {
    const response = await api.get(url)
    return mapSessionResponse(response.data)
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    console.error('Error fetching latest session:', error)
    throw error
  }
}

// ==================== MESSAGING ====================

export const sendProjectStreamingConversation = async (
  params: SendProjectMessageParams,
  callbacks: StreamCallbacks,
  abortSignal?: AbortSignal,
): Promise<void> => {
  const { userId, tenantId, projectId, sessionId, message } = params
  const url = `/chatbot/users/${userId}/tenants/${tenantId}/projects/${projectId}/sessions/${sessionId}/response`
  const fullUrl = `${api.defaults.baseURL}${url}`

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: await copaHeaders(),
      body: message ? JSON.stringify({ message }) : undefined,
      signal: abortSignal,
    })

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}: ${response.statusText}`)
    }

    await readSSEStream(response, callbacks)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.log('Stream request was cancelled')
    } else {
      console.error('Error in project streaming conversation:', error)
      if (callbacks.onError) {
        callbacks.onError(error instanceof Error ? error : new Error('Unknown error'))
      }
      throw error
    }
  }
}

export const sendTenantStreamingConversation = async (
  params: SendTenantMessageParams,
  callbacks: StreamCallbacks,
  abortSignal?: AbortSignal,
): Promise<void> => {
  const { userId, tenantId, sessionId, message } = params
  const url = `/chatbot/users/${userId}/tenants/${tenantId}/sessions/${sessionId}/response`
  const fullUrl = `${api.defaults.baseURL}${url}`

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: await copaHeaders(),
      body: message ? JSON.stringify({ message }) : undefined,
      signal: abortSignal,
    })

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}: ${response.statusText}`)
    }

    await readSSEStream(response, callbacks)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.log('Stream request was cancelled')
    } else {
      console.error('Error in tenant streaming conversation:', error)
      if (callbacks.onError) {
        callbacks.onError(error instanceof Error ? error : new Error('Unknown error'))
      }
      throw error
    }
  }
}

// ==================== COPA PROACTIVE RESPONSE ====================

export const triggerCopaProactiveResponse = async (
  params: { userId: string; tenantId: string; sessionId: string | null },
  callbacks: CopaProactiveResponseCallbacks,
  abortSignal?: AbortSignal,
): Promise<void> => {
  let activeSessionId = params.sessionId
  if (!activeSessionId) {
    const session = await createUserSession({ userId: params.userId })
    // Aborted while the session was being created: the chat it was meant for is gone
    if (!session.id || abortSignal?.aborted) return
    activeSessionId = session.id
    callbacks.onSessionCreated(activeSessionId)
  }

  const assistantMessageId = uuidv4()
  callbacks.onMessageAdded(assistantMessageId)

  let firstChunkReceived = false
  await sendTenantStreamingConversation(
    { userId: params.userId, tenantId: params.tenantId, sessionId: activeSessionId },
    {
      onChunk: (chunk) => {
        if (!firstChunkReceived) {
          firstChunkReceived = true
          callbacks.onFirstChunk()
        }
        callbacks.onChunk(chunk)
      },
      onThinking: callbacks.onThinking,
      onThinkingDone: callbacks.onThinkingDone,
      onError: () => callbacks.onError(assistantMessageId),
    },
    abortSignal,
  ).catch((err) => {
    if (err?.name === 'AbortError') return
    console.error('Error sending Copa proactive response:', err)
    callbacks.onError(assistantMessageId)
  })
}
